/**
 * StorageBreakdown — what is using a workspace's file storage (file areas
 * §4.9): Documents, Arche Saves and My-Fias, each split by the arche the
 * files belong to, with earlier versions shown apart from current files.
 *
 * It counts exactly what the storage meter counts, so its total is the
 * usage bar's number — nothing the user pays for is left unexplained.
 * Fetched only when opened.
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { VaultStorageAreaDto } from '@/lib/api';
import { formatBytes } from '@/components/files/file-dialog/format';
import { archeLabel } from '../lib/arche-labels';
import { useVaultStorageBreakdown } from '../hooks/useVaultDocuments';

const AREA_LABELS: Record<VaultStorageAreaDto['area'], { label: string; hint: string }> = {
  documents: { label: 'Documents', hint: 'Your own files and folders.' },
  'arche-saves': {
    label: 'Arche Saves',
    hint: 'Files arches delivered for you — AI images, email attachments, signed PDFs.',
  },
  'my-fias': {
    label: 'My-Fias',
    hint: 'Files arches keep for their own use, and files you gave an arche to work with.',
  },
};

export function StorageBreakdown({ holderId }: { holderId: string | null }) {
  const [open, setOpen] = useState(false);
  const query = useVaultStorageBreakdown(holderId, open);
  if (!holderId) return null;

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 self-start text-xs text-muted-foreground underline"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        )}
        What’s using space
      </button>
      {open && query.isLoading && <p className="text-xs text-muted-foreground">Adding it up…</p>}
      {open && query.isError && (
        <p role="alert" className="text-xs text-destructive">
          Couldn’t load the breakdown. Try again later.
        </p>
      )}
      {open && query.data && (
        <div className="rounded border border-border bg-card p-3 text-sm">
          <ul className="flex flex-col gap-3">
            {query.data.areas.map((area) => (
              <li key={area.area}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-medium text-foreground">
                    {AREA_LABELS[area.area].label}
                  </span>
                  <span className="text-muted-foreground">
                    {formatBytes(area.bytes + area.historyBytes)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{AREA_LABELS[area.area].hint}</p>
                <p className="text-xs text-muted-foreground">
                  {area.fileCount} {area.fileCount === 1 ? 'file' : 'files'} ·{' '}
                  {formatBytes(area.bytes)}
                  {area.historyBytes > 0 &&
                    ` · earlier versions: ${formatBytes(area.historyBytes)} (${area.historyCount})`}
                </p>
                {area.byArche.length > 0 && (
                  <ul className="mt-1 flex flex-col gap-0.5 pl-3">
                    {area.byArche.map((entry) => (
                      <li
                        key={entry.archeId ?? '(none)'}
                        className="flex items-baseline justify-between gap-3 text-xs"
                      >
                        <span className="truncate text-foreground">
                          {archeLabel(entry, area.area)}
                        </span>
                        <span className="shrink-0 text-muted-foreground">
                          {formatBytes(entry.bytes + entry.historyBytes)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
          {query.data.restricted.fileCount > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              Files you can’t open here: {query.data.restricted.fileCount}{' '}
              {query.data.restricted.fileCount === 1 ? 'file' : 'files'} ·{' '}
              {formatBytes(query.data.restricted.bytes)}
            </p>
          )}
          <p className="mt-3 border-t border-border pt-2 text-xs text-muted-foreground">
            Total {formatBytes(query.data.totalBytes)} — files in the Trash don’t count.
          </p>
        </div>
      )}
    </div>
  );
}
